import { ConnectProto } from "@racoon/api";

import { enumNumber } from "./job-enums";

const { JobStatus } = ConnectProto;

/** The outcomes an ended job can be corrected between, in the dropdown's order. */
export const OUTCOMES = [
  JobStatus.COMPLETED,
  JobStatus.NOT_COMPLETED,
  JobStatus.CANCELLED,
] as const;

export type Outcome = (typeof OUTCOMES)[number];

/**
 * The outcome a job ended in, or undefined while it is under way. A force-completed job
 * reads as Выполнено: both carry that label, so the dropdown cannot tell them apart.
 */
export const outcomeOf = (status: ConnectProto.JobStatus | string): Outcome | undefined => {
  const value = enumNumber(ConnectProto.JobStatusSchema, status);
  if (value === JobStatus.FORCE_COMPLETED) return JobStatus.COMPLETED;
  return OUTCOMES.find((outcome) => outcome === value);
};

/** The statuses of a retry job no truck has set out for yet — the only ones still cancellable. */
export const WAITING_STATUSES: ConnectProto.JobStatus[] = [JobStatus.CREATED, JobStatus.ASSIGNED];

/** ListJobs for the attempts at `job`'s order: a few jobs, never a capped page. */
export const orderAttemptsQuery = (job: ConnectProto.Job) => ({
  orderNumbers: [job.orderNumber!],
  serviceProviderIds: [job.serviceProviderId],
  serviceReceiverIds: [job.serviceReceiverId],
});

/** `job`'s waiting retry jobs: same order number and type (legacy imports reuse numbers). */
export const findRetryJobs = (
  job: ConnectProto.Job,
  candidates: ConnectProto.Job[],
): ConnectProto.Job[] => {
  if (!job.orderNumber) return [];
  return candidates.filter((candidate) => {
    const status = enumNumber(ConnectProto.JobStatusSchema, candidate.status);
    return (
      candidate.id !== job.id &&
      candidate.orderNumber === job.orderNumber &&
      enumNumber(ConnectProto.JobTypeSchema, candidate.type) ===
        enumNumber(ConnectProto.JobTypeSchema, job.type) &&
      WAITING_STATUSES.some((waiting) => waiting === status)
    );
  });
};

/**
 * The UpdateJob a verdict press sends: the verdict, and the staged outcome when it corrects
 * the job's own — an outcome correction is never saved on its own.
 */
export const buildVerdictUpdate = (
  job: ConnectProto.Job,
  verdict: ConnectProto.DispatcherReviewStatus,
  staged?: Outcome,
) => ({
  id: job.id,
  dispatcherReviewStatus: verdict,
  // No fact_end_at: the backend dates a corrected job itself
  ...(staged !== undefined && staged !== outcomeOf(job.status) && { status: staged }),
});

/** A correction out of Невывоз: the backend refuses Выполнено while the order's retry job is open. */
export const leavesNotCompleted = (job: ConnectProto.Job, status?: Outcome): boolean =>
  status !== undefined && outcomeOf(job.status) === JobStatus.NOT_COMPLETED;
